import { IDestroyOptions, TextStyle } from "pixi.js";
import { ButtonWithScaleAnimation } from "./ButtonWithScaleAnimation";
import { SoundsResourcePackageWrapper } from "../resources/SoundsResourcePackageWrapper";
import { Event } from "event-system";
import { GameResourcePackage } from "../../game/resources/GameResourcePackage";
import { TextWithAdaptiveTime } from "./TextWithAdaptiveTime";
import { ITournamentModel } from "../models/tournament/ITournamentModel";
import { nameof } from "../../../../shared/bindable-data";
import { TournamentState } from "../models/tournament/TournamentState";
import { GameFont } from "../GameFont";

export class TournamentButton extends ButtonWithScaleAnimation {
    public static readonly MILLISECONDS_IN_DAY = 86400000/*ms*/;
    public static readonly INTERACTIVE_BUTTON_IN_FINISHED_DAY = 5/*d*/;

    private static readonly ENDS_IN_TEXT = "ENDS IN\n";
    private static readonly STARTS_IN_TEXT = "STARTS IN\n";
    private static readonly ENDED_IN_TEXT = "ENDED\n";

    public readonly onButtonClick: Event<void>;

    private textWithTime: TextWithAdaptiveTime;

    public constructor(
        gameResources: GameResourcePackage,
        private readonly soundResources: SoundsResourcePackageWrapper,
        private readonly tournamentModel: ITournamentModel
    ) {
        super(gameResources.assets_views_game_ui_tournament_icon_png);
        this.soundResources = soundResources;
        this.onButtonClick = new Event();
        this.build();
        this.tournamentModel.propertyChanged.add(this.onModelPropertyChange, this);
        this.onUp.add(this.clickHandle, this);
    }

    private onModelPropertyChange(model: ITournamentModel, propertyName: string): void {
        switch (propertyName) {
            case nameof(model, "state"):
            case nameof(model, "time"):
                this.updateText();
                const days = model.time.value / TournamentButton.MILLISECONDS_IN_DAY;
                this.interactive = !(model.state.value == TournamentState.FINISHED && days >= TournamentButton.INTERACTIVE_BUTTON_IN_FINISHED_DAY) && model.state.value != TournamentState.PLANNED;
                break;
        }
    }

    private build(): void {
        this.textWithTime = new TextWithAdaptiveTime(this.tournamentModel.time.value, TournamentButton.ENDS_IN_TEXT, TournamentButton.ENDED_IN_TEXT, new LobbyStyle());
        this.updateText();
    }

    private updateText(): void {
        const time = this.tournamentModel.time.value;
        const isTimer = this.tournamentModel.state.value != TournamentState.UNAVAILABLE && time != null;
        if (isTimer) {
            this.addChild(this.textWithTime);
            this.textWithTime.updateDefaultText(this.getTimerDefaultText());
            this.textWithTime.updateTime(this.tournamentModel.state.value != TournamentState.FINISHED ? time : 0);
        }
        else {
            this.removeChild(this.textWithTime);
        }
        this.alignText();
    }

    private getTimerDefaultText(): string {
        if (this.tournamentModel.state.value === TournamentState.PLANNED) {
            return TournamentButton.STARTS_IN_TEXT;
        }
        if (this.tournamentModel.state.value === TournamentState.FINISHED) {
            return TournamentButton.ENDED_IN_TEXT;
        }
        return TournamentButton.ENDS_IN_TEXT;
    }

    private alignText(): void {
        this.textWithTime.position.set((this.defaultView.width - this.textWithTime.width) * 0.5 + 5, this.defaultView.height + 4);
    }

    private async clickHandle(): Promise<void> {
        this.soundResources.assets_sounds_Choose_Card_mp3.play();
        this.onButtonClick.emit();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.tournamentModel.propertyChanged.remove(this.onModelPropertyChange, this);
        this.onUp.remove(this.clickHandle, this);
        super.destroy(options);
    }
}

class LobbyStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#253480",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32,
            stroke: 0xffffff,
            strokeThickness: 4
        });
    }
}
